import { Component, Inject, OnInit } from '@angular/core';
import {
  FormArray,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { RuleInterface } from '@vendasta/automata';
import { Observable } from 'rxjs';
import { insertVariable } from '../../../../../common/components/automation-variable-menu/automation-variable-menu.service';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import {
  AutomataContextService,
  AUTOMATION_NAMESPACE_INJECTION_TOKEN$,
  ContextConfigMap,
} from '@galaxy/automata/shared';
import { CommonActionPanelComponent } from '../../../common/common-action-panel-component.directive';
import { secureUrlValidator, urlValidator } from '../../../common/utils/url-validator';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { StaticSelectorService } from '../../../../../data-services/static-selector.service';
import { AutomationReplacementChipsComponent } from '../../../../../common/components';
import {
  KeyValue,
  KeyValueForm,
  KeyValueInputComponent,
} from '../../../common/controls/key-value-input/key-value-input.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

const methodChoice = new Map<string, string>([
  ['POST', 'AUTOMATIONS.EDITOR.TASKS.OUTGOING_WEBHOOK.PANEL.METHOD_POST'],
  ['GET', 'AUTOMATIONS.EDITOR.TASKS.OUTGOING_WEBHOOK.PANEL.METHOD_GET'],
  ['PUT', 'AUTOMATIONS.EDITOR.TASKS.OUTGOING_WEBHOOK.PANEL.METHOD_PUT'],
  ['DELETE', 'AUTOMATIONS.EDITOR.TASKS.OUTGOING_WEBHOOK.PANEL.METHOD_DELETE'],
]);

interface Cookie {
  name: string;
  value: string;
}

@Component({
  selector: 'automata-outgoing-webhook-panel',
  templateUrl: './outgoing-webhook-panel.component.html',
  styleUrls: ['./outgoing-webhook-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    AutomationReplacementChipsComponent,
    KeyValueInputComponent,
    MatExpansionModule,
    ClipboardModule,
  ],
})
export class OutgoingWebhookPanelComponent extends CommonActionPanelComponent implements OnInit {
  public httpMethodService = new StaticSelectorService(methodChoice, this.translateService);

  form: UntypedFormGroup = new UntypedFormGroup({
    endpoint_url: new FormControl<string>('', [urlValidator, secureUrlValidator, Validators.required]),
    method: new FormControl<string>('POST', [Validators.required]),
    json_body: new FormArray<FormGroup<KeyValueForm>>([]),
    custom_headers: new FormArray<FormGroup<KeyValueForm>>([]),
    query_params: new FormArray<FormGroup<KeyValueForm>>([]),
    cookies: new FormArray<FormGroup<KeyValueForm>>([]),
  });
  public automationContext = this.automationContextService.currentContext;
  contextConfig = ContextConfigMap[this.automationContext];

  constructor(
    private readonly automationContextService: AutomataContextService,
    @Inject(AUTOMATION_NAMESPACE_INJECTION_TOKEN$) public readonly namespace$: Observable<string>,
    protected readonly automationService: AutomationViewService,
    protected readonly snackbarService: SnackbarService,
    protected readonly translateService: TranslateService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    this.form.controls.endpoint_url.patchValue(jsonData.endpoint_url?.replace('https://', '') || '');
    this.form.controls.method.patchValue(jsonData.method || 'POST');
    Object.entries(jsonData.custom_headers || {}).forEach(([k, v]) =>
      (this.form.controls.custom_headers as FormArray).push(
        new FormGroup<KeyValueForm>({
          key: new FormControl<string>(String(k)),
          value: new FormControl<string>(String(v)),
        }),
      ),
    );
    Object.entries(jsonData.query_params || {}).forEach(([k, v]) =>
      (this.form.controls.query_params as FormArray).push(
        new FormGroup<KeyValueForm>({
          key: new FormControl<string>(String(k)),
          value: new FormControl<string>(String(v)),
        }),
      ),
    );
    (jsonData.cookies || []).forEach((c: Cookie) =>
      (this.form.controls.cookies as FormArray).push(
        new FormGroup<KeyValueForm>({
          key: new FormControl<string>(String(c.name)),
          value: new FormControl<string>(String(c.value)),
        }),
      ),
    );
    Object.entries(jsonData.json_body || {}).forEach(([k, v]) =>
      (this.form.controls.json_body as FormArray).push(
        new FormGroup<KeyValueForm>({
          key: new FormControl<string>(String(k)),
          value: new FormControl<string>(String(v)),
        }),
      ),
    );

    // Since there's nothing asynchronous about this form, saving is enabled on initialization.
    this.savingEnabled = true;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.form];
  }

  getData(): any {
    const jsonData = this.step ? JSON.parse(this.step.data) : {};

    if (!this.form) {
      return jsonData;
    }
    jsonData.endpoint_url = this.ensureUrlStartsWithHttps(this.form.controls.endpoint_url.value || '');
    jsonData.method = this.form.controls.method.value;

    const custom_headers: { [key: string]: string } = {};
    (this.form.controls.custom_headers.value || []).forEach((fg: KeyValue) => {
      custom_headers[fg.key] = fg.value;
    });
    jsonData.custom_headers = custom_headers;

    const query_params: { [key: string]: string } = {};
    (this.form.controls.query_params.value || []).forEach((fg: KeyValue) => {
      query_params[fg.key] = fg.value;
    });
    jsonData.query_params = query_params;

    const cookies: Cookie[] = [];
    (this.form.controls.cookies.value || []).forEach((fg: KeyValue) => {
      cookies.push({ name: fg.key, value: fg.value });
    });
    jsonData.cookies = cookies;

    const json_body: { [key: string]: string } = {};
    (this.form.controls.json_body.value || []).forEach((fg: KeyValue) => {
      json_body[fg.key] = fg.value;
    });
    jsonData.json_body = json_body;
    return jsonData;
  }

  getRules(): RuleInterface[] {
    return [];
  }

  isFormValid(): boolean {
    return this.form.valid;
  }

  ensureUrlStartsWithHttps(url: string): string {
    if (url.startsWith('https://')) {
      return url;
    }
    return 'https://' + url;
  }

  insertVariable(elementID: string, control: any, variable: string): void {
    insertVariable(elementID, control, variable);
  }

  generateCurlCommand(): string {
    let url = new URL('https://example.com');
    try {
      url = new URL(this.ensureUrlStartsWithHttps(this.form.controls.endpoint_url.value || ''));
    } catch (error) {
      //ignore the error and use the default url
    }

    (this.form.controls.query_params.value || []).forEach((qp: KeyValue) => url.searchParams.append(qp.key, qp.value));

    const formHeaders = JSON.parse(JSON.stringify(this.form.controls.custom_headers.value || []));
    formHeaders.push({ key: 'Content-Type', value: 'application/json' });
    const headers = formHeaders.map((h: KeyValue) => `-H '${h.key}: ${h.value}'`).join(' ');

    const cookies = (this.form.controls.cookies.value || [])
      .map((c: KeyValue) => `--cookie '${c.key}=${c.value}'`)
      .join(' ');
    const jsonBodyObj = (this.form.controls.json_body.value || []).reduce((acc: any, jb: KeyValue) => {
      acc[jb.key] = jb.value;
      return acc;
    }, {});
    const json_body = `-d '${JSON.stringify(jsonBodyObj)}'`;

    return `curl -X ${this.form.controls.method.value} ${headers} ${cookies} ${json_body} --location ${url.href}`;
  }

  reassureTheUserThatTheButtonDidSomething(): void {
    this.snackbarService.openSuccessSnack(
      this.translateService.instant('AUTOMATIONS.EDITOR.TASKS.OUTGOING_WEBHOOK.PANEL.CURL_COPIED'),
    );
  }
}
